import {
  ConversionCards,
  ConversionComparison,
  ConversionMetrics,
  ConversionRatio,
  ConversionWirePeriod,
} from '@partner/feature-modules/dashboard/domain/model/conversion-metrics.model';
import { IsoDate } from '@partner/feature-modules/dashboard/domain/model/dashboard-filters.model';
import { PipelineRatioResponse } from './pipeline-health-api.model';

/**
 * 200 body of `GET api/dashboard/conversion-metrics` (contract v1.1); instants are ISO-8601 UTC strings.
 * `comparison` and `deltaPoints` are optional because a v1.0 server omits them.
 */
export interface ConversionMetricsResponse {
  asOfUtc: string;
  period: { from: IsoDate; to: IsoDate };
  cards: ConversionCardsResponse;
  comparison?: ConversionComparisonResponse | null;
  /** Envelope symmetry with the sibling responses; always null on 200. */
  validationMessage: string | null;
}

export interface ConversionRatioResponse extends PipelineRatioResponse {
  deltaPoints?: number | null;
}

export interface ConversionCardsResponse {
  submittedToApproved: ConversionRatioResponse;
  approvedToFunded: ConversionRatioResponse;
  startedToFunded: ConversionRatioResponse;
}

/** The prior pairs are on the wire for QA; the client does not read them. */
export interface ConversionComparisonResponse {
  period: ConversionWirePeriod;
  from: IsoDate;
  to: IsoDate;
  cutoffUtc: string;
  cards: {
    submittedToApproved: PipelineRatioResponse;
    approvedToFunded: PipelineRatioResponse;
    startedToFunded: PipelineRatioResponse;
  };
}

/** `??` turns a missing value into null and leaves a real 0 alone; the counts are never omitted, so not guarded. */
const adaptRatio = (ratio: ConversionRatioResponse): ConversionRatio => ({
  percent: ratio.percent ?? null,
  numerator: ratio.numerator,
  denominator: ratio.denominator,
  deltaPoints: ratio.deltaPoints ?? null,
});

const adaptCards = (cards: ConversionCardsResponse): ConversionCards => ({
  submittedToApproved: adaptRatio(cards.submittedToApproved),
  approvedToFunded: adaptRatio(cards.approvedToFunded),
  startedToFunded: adaptRatio(cards.startedToFunded),
});

const adaptComparison = (comparison: ConversionComparisonResponse | null | undefined): ConversionComparison | null =>
  comparison
    ? {
        period: comparison.period,
        from: comparison.from,
        to: comparison.to,
        cutoffUtc: new Date(comparison.cutoffUtc),
      }
    : null;

export const adaptConversionMetrics = (response: ConversionMetricsResponse): ConversionMetrics => ({
  asOfUtc: new Date(response.asOfUtc),
  period: { from: response.period.from, to: response.period.to },
  cards: adaptCards(response.cards),
  comparison: adaptComparison(response.comparison),
});
